import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useRef } from 'react';
import { useMotionPreferences } from './preferences';

gsap.registerPlugin(useGSAP);

/**
 * A small, scoped GSAP orchestration for report beats. It deliberately animates
 * only the explanatory sequence: label → question → evidence → conclusion.
 */
export function useBeatOrchestration({ active, presenter = false }: { active: boolean; presenter?: boolean }) {
  const root = useRef<HTMLDivElement>(null);
  const { reducedMotion } = useMotionPreferences();
  const lifecycleKey = presenter ? active : true;

  useGSAP(() => {
    if (presenter && !active) return;
    const element = root.current;
    if (!element) return;
    const select = gsap.utils.selector(element);
    const transition = select('[data-section-transition]');
    const kicker = select('[data-beat-kicker]');
    const question = select('[data-beat-question]');
    const dots = select('[data-beat-dots]');
    const visual = select('[data-beat-visual]');
    const conclusion = select('[data-beat-conclusion]');
    const revealItems = select('[data-reveal-item]');
    const allTargets = [...transition, ...kicker, ...question, ...dots, ...visual, ...conclusion, ...revealItems];

    if (reducedMotion) {
      gsap.set(allTargets, {
        autoAlpha: 1,
        clearProps: 'transform',
      });
      element.dataset.revealed = 'true';
      return;
    }

    gsap.set(transition, { autoAlpha: 0, scaleX: 0, transformOrigin: 'left center' });
    gsap.set(kicker, { autoAlpha: 0, y: 10 });
    gsap.set(question, { autoAlpha: 0, y: 22 });
    gsap.set(dots, { autoAlpha: 0, y: 12 });
    gsap.set(visual, { autoAlpha: 0, y: 24, scale: 0.985 });
    gsap.set(conclusion, { autoAlpha: 0, y: 14 });
    if (revealItems.length) gsap.set(revealItems, { autoAlpha: 0, y: 12 });

    const timeline = gsap.timeline({
      paused: true,
      defaults: { ease: 'power3.out', overwrite: 'auto' },
      onStart: () => { element.dataset.revealing = 'true'; },
      onComplete: () => {
        element.dataset.revealed = 'true';
        delete element.dataset.revealing;
        gsap.set(allTargets, { clearProps: 'transform' });
      },
    });
    timeline
      .addLabel('enter', 0)
      .to(transition, { autoAlpha: 1, scaleX: 1, duration: 0.34 }, 'enter')
      .to(kicker, { autoAlpha: 1, y: 0, duration: 0.2 }, 'enter+=0.04')
      .to(question, { autoAlpha: 1, y: 0, duration: 0.4 }, 'enter+=0.08')
      .to(dots, { autoAlpha: 1, y: 0, duration: 0.3 }, 'enter+=0.16')
      .to(visual, { autoAlpha: 1, y: 0, scale: 1, duration: 0.46 }, 'enter+=0.22');
    if (revealItems.length) {
      timeline.to(revealItems, { autoAlpha: 1, y: 0, duration: 0.3, stagger: 0.04 }, 'enter+=0.28');
    }
    timeline.to(conclusion, { autoAlpha: 1, y: 0, duration: 0.34 }, 'enter+=0.36');

    if (presenter || typeof IntersectionObserver === 'undefined') {
      timeline.play(0);
      return () => timeline.kill();
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || element.dataset.revealed === 'true') return;
      observer.unobserve(element);
      timeline.play(0);
    }, { rootMargin: '0px 0px -14%', threshold: 0.16 });
    observer.observe(element);
    return () => {
      observer.disconnect();
      timeline.kill();
    };
  }, { scope: root, dependencies: [lifecycleKey, presenter, reducedMotion], revertOnUpdate: true });

  return root;
}

export function useChapterHeadingOrchestration({ active, presenter = false }: { active: boolean; presenter?: boolean }) {
  const root = useRef<HTMLDivElement>(null);
  const { reducedMotion } = useMotionPreferences();
  const lifecycleKey = presenter ? active : true;

  useGSAP(() => {
    if (presenter && !active) return;
    const element = root.current;
    if (!element) return;
    const headingParts = gsap.utils.selector(element)('[data-chapter-heading-part]');
    if (reducedMotion) {
      gsap.set(headingParts, { autoAlpha: 1, clearProps: 'transform' });
      return;
    }

    gsap.set(headingParts, { autoAlpha: 0, y: 20 });
    const timeline = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } })
      .to(headingParts, {
        autoAlpha: 1,
        y: 0,
        duration: 0.46,
        stagger: 0.055,
      });
    const play = () => timeline.play(0);
    if (presenter || typeof IntersectionObserver === 'undefined') {
      play();
      return () => timeline.kill();
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      observer.unobserve(element);
      play();
    }, { rootMargin: '0px 0px -18%', threshold: 0.25 });
    observer.observe(element);
    return () => { observer.disconnect(); timeline.kill(); };
  }, { scope: root, dependencies: [lifecycleKey, presenter, reducedMotion], revertOnUpdate: true });

  return root;
}
